/**
 * Call Post-Processing Workflow
 *
 * This workflow automatically processes call recordings:
 * 1. Detects when a new call is created
 * 2. Checks if a recording exists
 * 3. Transcribes the recording
 * 4. Extracts action items and commitments
 * 5. Creates tasks from action items
 * 6. Notifies user of results
 */

import type { Workflow } from './types';

// =============================================================================
// WORKFLOW DEFINITION
// =============================================================================

export const callPostProcessingWorkflow: Workflow = {
  id: 'call-post-processing',
  name: 'Post-Elaborazione Chiamata',
  description: 'Trascrive chiamate registrate, estrae azioni e crea task automaticamente',
  trigger: {
    type: 'event',
    config: {
      eventType: 'entity.created',
      entityType: 'call',
    },
  },
  steps: [
    {
      id: 'check_recording',
      name: 'Verifica Registrazione',
      type: 'custom',
      config: {
        checkField: 'recordingUrl',
        continueIfMissing: false,
      },
      condition: {
        field: 'recordingUrl',
        operator: 'exists',
      },
      next: ['transcribe'],
      onError: 'stop',
    },
    {
      id: 'transcribe',
      name: 'Trascrivi Chiamata',
      type: 'transcribe',
      config: {
        language: 'it',
        enablePunctuation: true,
        enableTimestamps: true,
      },
      next: ['extract_action_items'],
    },
    {
      id: 'extract_action_items',
      name: 'Estrai Azioni',
      type: 'ai_analyze',
      config: {
        prompt: `Analyze this call transcript and extract all action items and commitments.

For each action item, identify:
- What needs to be done
- Who committed to doing it
- Any mentioned deadline
- Priority level based on context

Respond in JSON:
{
  "actionItems": [
    {
      "description": "Action description",
      "assignee": "person name or null",
      "dueDate": "date if mentioned or null",
      "priority": "high|medium|low"
    }
  ],
  "summary": "Brief call summary"
}`,
      },
      next: ['create_tasks'],
    },
    {
      id: 'create_tasks',
      name: 'Crea Task',
      type: 'create_tasks',
      config: {
        projectIdField: 'relatedProjectId',
        tasksField: 'actionItems',
        linkToCall: true,
      },
      next: ['send_notification'],
    },
    {
      id: 'send_notification',
      name: 'Notifica Completamento',
      type: 'send_notification',
      config: {
        type: 'in_app',
        message: 'Chiamata trascritta! Ho creato {taskCount} task dalle azioni discusse.',
        includeTranscript: true,
      },
    },
  ],
  enabled: true,
  version: 1,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};
